/**
 * Ludo Board Component
 *
 * Renders the cross-shaped Ludo board with shared track (52 squares),
 * 4 home bases, 4 home stretches (6 each), safe spots, and home triangle.
 * Uses CSS Grid for the cross-shaped layout.
 */

import React, { useMemo } from 'react';
import {
  PlayerColor,
  TokenIdentifier,
  SAFE_SPOTS,
  PLAYER_START_POSITIONS,
  PLAYER_COLORS,
} from '../types';
import { LudoEngine } from '../LudoEngine';
import './LudoBoard.css';

interface LudoBoardProps {
  engine: LudoEngine;
  onTokenSelect: (player: PlayerColor, tokenIndex: number) => void;
  highlightedTokens: TokenIdentifier[];
  currentTurn: PlayerColor;
  isInteractive: boolean;
  playerCount: number;
  gamePhase: 'waiting' | 'rolling' | 'moving' | 'animating' | 'completed';
}

/**
 * The board is a 15×15 grid. The cross shape occupies:
 * - Top arm: cols 6-8, rows 0-5
 * - Left arm: cols 0-5, rows 6-8
 * - Center: cols 6-8, rows 6-8
 * - Right arm: cols 9-14, rows 6-8
 * - Bottom arm: cols 6-8, rows 9-14
 * Home bases occupy the four corners (6×6 each).
 */

// Map absolute track positions (0-51) to grid coordinates [row, col]
const TRACK_POSITIONS: Record<number, [number, number]> = {};

// Build the track path clockwise starting from position 0 (Red's start)
// Red starts at top of left arm, goes right
const trackPath: [number, number][] = [
  // Red start → going right along row 6 (positions 0-4)
  [6, 1], [6, 2], [6, 3], [6, 4], [6, 5],
  // Turn: go up column 6 (positions 5-10)
  [5, 6], [4, 6], [3, 6], [2, 6], [1, 6], [0, 6],
  // Turn right along top (positions 11-12)
  [0, 7], [0, 8],
  // Blue start → going down column 8 (positions 13-17)
  [1, 8], [2, 8], [3, 8], [4, 8], [5, 8],
  // Turn: go right along row 6 (positions 18-23)
  [6, 9], [6, 10], [6, 11], [6, 12], [6, 13], [6, 14],
  // Turn down along right (positions 24-25)
  [7, 14], [8, 14],
  // Green start → going left along row 8 (positions 26-30)
  [8, 13], [8, 12], [8, 11], [8, 10], [8, 9],
  // Turn: go down column 8 (positions 31-36)
  [9, 8], [10, 8], [11, 8], [12, 8], [13, 8], [14, 8],
  // Turn left along bottom (positions 37-38)
  [14, 7], [14, 6],
  // Yellow start → going up column 6 (positions 39-43)
  [13, 6], [12, 6], [11, 6], [10, 6], [9, 6],
  // Turn: go left along row 8 (positions 44-49)
  [8, 5], [8, 4], [8, 3], [8, 2], [8, 1], [8, 0],
  // Turn up along left (positions 50-51)
  [7, 0], [6, 0],
];

// Fill the TRACK_POSITIONS map
trackPath.forEach(([row, col], index) => {
  TRACK_POSITIONS[index] = [row, col];
});

// Home stretch positions for each player (6 squares leading to center)
const HOME_STRETCH_POSITIONS: Record<PlayerColor, [number, number][]> = {
  red: [[7, 1], [7, 2], [7, 3], [7, 4], [7, 5], [7, 6]],
  blue: [[1, 7], [2, 7], [3, 7], [4, 7], [5, 7], [6, 7]],
  green: [[7, 13], [7, 12], [7, 11], [7, 10], [7, 9], [7, 8]],
  yellow: [[13, 7], [12, 7], [11, 7], [10, 7], [9, 7], [8, 7]],
};

// Home base token positions (4 tokens in each corner)
const HOME_BASE_POSITIONS: Record<PlayerColor, [number, number][]> = {
  red: [[1, 1], [1, 3], [3, 1], [3, 3]],
  blue: [[1, 11], [1, 13], [3, 11], [3, 13]],
  green: [[11, 11], [11, 13], [13, 11], [13, 13]],
  yellow: [[11, 1], [11, 3], [13, 1], [13, 3]],
};

// Home triangle center
const HOME_CENTER: [number, number] = [7, 7];

const COLOR_MAP: Record<PlayerColor, string> = {
  red: 'var(--ludo-red, #e53935)',
  blue: 'var(--ludo-blue, #1e88e5)',
  green: 'var(--ludo-green, #43a047)',
  yellow: 'var(--ludo-yellow, #fdd835)',
};

const LudoBoard: React.FC<LudoBoardProps> = ({
  engine,
  onTokenSelect,
  highlightedTokens,
  currentTurn,
  isInteractive,
  playerCount,
  gamePhase,
}) => {
  const gameState = engine.getGameState();
  const activePlayers = gameState.activePlayers;

  // Build set of highlighted token IDs for quick lookup
  const highlightedSet = useMemo(() => {
    const set = new Set<string>();
    highlightedTokens.forEach((t) => set.add(`${t.player}-${t.tokenIndex}`));
    return set;
  }, [highlightedTokens]);

  // Determine which cells are part of the cross
  const crossCells = useMemo(() => {
    const cells = new Set<string>();
    // Top arm: rows 0-5, cols 6-8
    for (let r = 0; r <= 5; r++) for (let c = 6; c <= 8; c++) cells.add(`${r}-${c}`);
    // Left arm: rows 6-8, cols 0-5
    for (let r = 6; r <= 8; r++) for (let c = 0; c <= 5; c++) cells.add(`${r}-${c}`);
    // Center: rows 6-8, cols 6-8
    for (let r = 6; r <= 8; r++) for (let c = 6; c <= 8; c++) cells.add(`${r}-${c}`);
    // Right arm: rows 6-8, cols 9-14
    for (let r = 6; r <= 8; r++) for (let c = 9; c <= 14; c++) cells.add(`${r}-${c}`);
    // Bottom arm: rows 9-14, cols 6-8
    for (let r = 9; r <= 14; r++) for (let c = 6; c <= 8; c++) cells.add(`${r}-${c}`);
    return cells;
  }, []);

  // Build safe spot set for track positions
  const safeSpotSet = useMemo(() => new Set(SAFE_SPOTS), []);

  // Build track position lookup (row-col → track position number)
  const trackPosLookup = useMemo(() => {
    const map = new Map<string, number>();
    Object.entries(TRACK_POSITIONS).forEach(([pos, [r, c]]) => {
      map.set(`${r}-${c}`, parseInt(pos));
    });
    return map;
  }, []);

  // Build home stretch lookup
  const homeStretchLookup = useMemo(() => {
    const map = new Map<string, { player: PlayerColor; index: number }>();
    for (const color of PLAYER_COLORS) {
      HOME_STRETCH_POSITIONS[color].forEach(([r, c], idx) => {
        map.set(`${r}-${c}`, { player: color, index: idx });
      });
    }
    return map;
  }, []);

  // Build home base slot lookup (cells where tokens start)
  const homeBaseSlotLookup = useMemo(() => {
    const map = new Map<string, PlayerColor>();
    for (const color of PLAYER_COLORS) {
      HOME_BASE_POSITIONS[color].forEach(([r, c]) => {
        map.set(`${r}-${c}`, color);
      });
    }
    return map;
  }, []);

  // Collect all tokens with their grid positions
  const tokenElements = useMemo(() => {
    const elements: Array<{
      player: PlayerColor;
      tokenIndex: number;
      row: number;
      col: number;
      highlighted: boolean;
    }> = [];

    for (const color of activePlayers) {
      const positions = gameState.tokens[color];
      for (const token of positions) {
        let row = -1, col = -1;

        if (token.location === 'home_base') {
          const basePos = HOME_BASE_POSITIONS[color][token.tokenIndex];
          if (basePos) {
            [row, col] = basePos;
          }
        } else if (token.location === 'track' && token.trackPosition !== undefined) {
          const pos = TRACK_POSITIONS[token.trackPosition];
          if (pos) {
            [row, col] = pos;
          }
        } else if (token.location === 'home_stretch' && token.homeStretchPosition !== undefined) {
          const pos = HOME_STRETCH_POSITIONS[color][token.homeStretchPosition];
          if (pos) {
            [row, col] = pos;
          }
        } else if (token.location === 'home') {
          [row, col] = HOME_CENTER;
        }

        if (row >= 0 && col >= 0) {
          elements.push({
            player: color,
            tokenIndex: token.tokenIndex,
            row,
            col,
            highlighted: highlightedSet.has(`${color}-${token.tokenIndex}`),
          });
        }
      }
    }

    return elements;
  }, [gameState, activePlayers, highlightedSet]);

  const handleTokenClick = (player: PlayerColor, tokenIndex: number) => {
    if (!isInteractive) return;
    if (highlightedSet.has(`${player}-${tokenIndex}`)) {
      onTokenSelect(player, tokenIndex);
    }
  };

  // Render the 15×15 grid
  const renderGrid = () => {
    const cells: React.ReactNode[] = [];

    for (let r = 0; r < 15; r++) {
      for (let c = 0; c < 15; c++) {
        const key = `${r}-${c}`;
        const isCross = crossCells.has(key);
        const trackPos = trackPosLookup.get(key);
        const homeStretch = homeStretchLookup.get(key);
        const isCenter = r === 7 && c === 7;

        // Home base areas (corners) — 5x5 colored area
        const isRedBase = r >= 0 && r <= 4 && c >= 0 && c <= 4 && !isCross;
        const isBlueBase = r >= 0 && r <= 4 && c >= 10 && c <= 14 && !isCross;
        const isGreenBase = r >= 10 && r <= 14 && c >= 10 && c <= 14 && !isCross;
        const isYellowBase = r >= 10 && r <= 14 && c >= 0 && c <= 4 && !isCross;
        const isHomeBase = isRedBase || isBlueBase || isGreenBase || isYellowBase;

        // White inner area (3x3) inside each home base for token circles
        const isRedInner = r >= 1 && r <= 3 && c >= 1 && c <= 3;
        const isBlueInner = r >= 1 && r <= 3 && c >= 11 && c <= 13;
        const isGreenInner = r >= 11 && r <= 13 && c >= 11 && c <= 13;
        const isYellowInner = r >= 11 && r <= 13 && c >= 1 && c <= 3;
        const isHomeInner = isHomeBase && (isRedInner || isBlueInner || isGreenInner || isYellowInner);

        let cellClass = 'ludo-board__cell';
        let cellColor = '';

        if (isHomeBase) {
          if (isHomeInner) {
            cellClass += ' ludo-board__cell--home-inner';
          } else {
            cellClass += ' ludo-board__cell--home-base';
            if (isRedBase) cellColor = 'red';
            else if (isBlueBase) cellColor = 'blue';
            else if (isGreenBase) cellColor = 'green';
            else if (isYellowBase) cellColor = 'yellow';
            cellClass += ` ludo-board__cell--${cellColor}`;
          }
        } else if (!isCross) {
          cellClass += ' ludo-board__cell--empty';
        } else if (isCenter) {
          cellClass += ' ludo-board__cell--center';
        } else if (homeStretch) {
          cellClass += ` ludo-board__cell--home-stretch ludo-board__cell--${homeStretch.player}`;
        } else if (trackPos !== undefined) {
          cellClass += ' ludo-board__cell--track';
          if (safeSpotSet.has(trackPos)) {
            cellClass += ' ludo-board__cell--safe';
            // Color the start squares
            for (const [color, startPos] of Object.entries(PLAYER_START_POSITIONS)) {
              if (trackPos === startPos) {
                cellClass += ` ludo-board__cell--start-${color}`;
              }
            }
          }
        } else if (isCross) {
          // Non-track cross cells at arm junctions — single solid color
          const isInnerCorner = (r === 6 && c === 6) || (r === 6 && c === 8) ||
                                (r === 8 && c === 6) || (r === 8 && c === 8);
          if (isInnerCorner) {
            cellClass += ' ludo-board__cell--corner-turn';
            // Each corner gets the color of the home stretch it's adjacent to
            if (r === 6 && c === 6) cellClass += ' ludo-board__cell--corner-red';    // adjacent to red HS
            else if (r === 6 && c === 8) cellClass += ' ludo-board__cell--corner-blue';   // adjacent to blue HS
            else if (r === 8 && c === 8) cellClass += ' ludo-board__cell--corner-green';  // adjacent to green HS
            else if (r === 8 && c === 6) cellClass += ' ludo-board__cell--corner-yellow'; // adjacent to yellow HS
          } else {
            cellClass += ' ludo-board__cell--track';
          }
        }

        cells.push(
          <div
            key={key}
            className={cellClass}
            style={{ gridRow: r + 1, gridColumn: c + 1 }}
            data-row={r}
            data-col={c}
            data-track-pos={trackPos}
            aria-label={
              trackPos !== undefined
                ? `Square ${trackPos}${safeSpotSet.has(trackPos) ? ' (safe)' : ''}`
                : homeStretch
                ? `${homeStretch.player} home stretch ${homeStretch.index + 1}`
                : isCenter
                ? 'Home'
                : undefined
            }
          >
            {trackPos !== undefined && safeSpotSet.has(trackPos) && (
              <span className="ludo-board__safe-marker" aria-hidden="true">★</span>
            )}
            {trackPos !== undefined && (
              <span className="ludo-board__pos-number" aria-hidden="true">{trackPos}</span>
            )}
            {/* Turn arrows showing direction of travel at corners */}
            {trackPos === 4 && <span className="ludo-board__turn-arrow" aria-hidden="true">↗</span>}
            {trackPos === 5 && <span className="ludo-board__turn-arrow" aria-hidden="true">↑</span>}
            {trackPos === 11 && <span className="ludo-board__turn-arrow" aria-hidden="true">↓</span>}
            {trackPos === 12 && <span className="ludo-board__turn-arrow" aria-hidden="true">↓</span>}
            {trackPos === 17 && <span className="ludo-board__turn-arrow" aria-hidden="true">↘</span>}
            {trackPos === 18 && <span className="ludo-board__turn-arrow" aria-hidden="true">→</span>}
            {trackPos === 24 && <span className="ludo-board__turn-arrow" aria-hidden="true">←</span>}
            {trackPos === 25 && <span className="ludo-board__turn-arrow" aria-hidden="true">←</span>}
            {trackPos === 30 && <span className="ludo-board__turn-arrow" aria-hidden="true">↙</span>}
            {trackPos === 31 && <span className="ludo-board__turn-arrow" aria-hidden="true">↓</span>}
            {trackPos === 37 && <span className="ludo-board__turn-arrow" aria-hidden="true">↑</span>}
            {trackPos === 38 && <span className="ludo-board__turn-arrow" aria-hidden="true">↑</span>}
            {trackPos === 43 && <span className="ludo-board__turn-arrow" aria-hidden="true">↖</span>}
            {trackPos === 44 && <span className="ludo-board__turn-arrow" aria-hidden="true">←</span>}
            {trackPos === 50 && <span className="ludo-board__turn-arrow" aria-hidden="true">→</span>}
            {trackPos === 51 && <span className="ludo-board__turn-arrow" aria-hidden="true">→</span>}
            {homeStretch && (
              <span className="ludo-board__pos-number ludo-board__pos-number--hs" aria-hidden="true">H{homeStretch.index}</span>
            )}
            {isHomeBase && homeBaseSlotLookup.has(key) && (
              <span className={`ludo-board__base-circle ludo-board__base-circle--${homeBaseSlotLookup.get(key)}`} aria-hidden="true" />
            )}
          </div>
        );
      }
    }

    return cells;
  };

  // Render tokens on top of the grid
  const renderTokens = () => {
    // Group tokens by position for stacking
    const positionGroups = new Map<string, typeof tokenElements>();
    for (const token of tokenElements) {
      const key = `${token.row}-${token.col}`;
      if (!positionGroups.has(key)) {
        positionGroups.set(key, []);
      }
      positionGroups.get(key)!.push(token);
    }

    const rendered: React.ReactNode[] = [];

    positionGroups.forEach((tokens, posKey) => {
      tokens.forEach((token, stackIndex) => {
        const offset = tokens.length > 1 ? (stackIndex - (tokens.length - 1) / 2) * 8 : 0;
        const isClickable = isInteractive && token.highlighted;

        rendered.push(
          <div
            key={`token-${token.player}-${token.tokenIndex}`}
            className={`ludo-board__token ludo-board__token--${token.player} ${
              token.highlighted ? 'ludo-board__token--highlighted' : ''
            } ${isClickable ? 'ludo-board__token--clickable' : ''}`}
            style={{
              gridRow: token.row + 1,
              gridColumn: token.col + 1,
              transform: `translate(${offset}%, ${-offset}%)`,
              zIndex: token.highlighted ? 10 : 5,
            }}
            onClick={() => handleTokenClick(token.player, token.tokenIndex)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && isClickable) {
                e.preventDefault();
                handleTokenClick(token.player, token.tokenIndex);
              }
            }}
            role={isClickable ? 'button' : undefined}
            tabIndex={isClickable ? 0 : undefined}
            aria-label={`${token.player} token ${token.tokenIndex + 1}${
              token.highlighted ? ' (can move)' : ''
            }`}
          >
            <span className="ludo-board__token-inner" aria-hidden="true" />
          </div>
        );
      });
    });

    return rendered;
  };

  return (
    <div className="ludo-board" role="grid" aria-label="Ludo board">
      <div className="ludo-board__grid">
        {renderGrid()}
        {renderTokens()}
      </div>
      <div className="ludo-board__branding" aria-hidden="true">
        <span className="ludo-board__branding-text">playhubgames.ai</span>
      </div>
    </div>
  );
};

export { LudoBoard };
export default LudoBoard;
